// src/services/authService.ts
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
    exp: number;
    sub: string;
    email: string;
    username: string;
}

interface AuthResponse {
    user: {
        id: string;
        email: string;
        username: string;
    };
    token: string;
}

type OAuthProvider = 'google' | 'microsoft' | 'linkedin';

class AuthService {
    private static instance: AuthService;
    private tokenKey = 'auth_token';
    private refreshTokenKey = 'refresh_token';
    private rememberedEmailKey = 'remembered_email';
    private oauthStateKey = 'oauth_state';

    private constructor() {}

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    // Mock login - replace with actual API call later
    async login(email: string, password: string, rememberMe: boolean = false): Promise<AuthResponse> {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Mock validation
        if (email === 'dennis@psinfoodservice.com' && password === 'password123') {
            const mockUser = {
                id: '1',
                email: 'dennis@psinfoodservice.com',
                username: 'Dennis',
                avatar: 'https://via.placeholder.com/150'
            };

            // Generate mock JWT token
            const token = this.generateMockToken(mockUser);
            const refreshToken = this.generateMockRefreshToken();

            // Store tokens
            this.setTokens(token, refreshToken);

            // Handle remember me
            if (rememberMe) {
                localStorage.setItem(this.rememberedEmailKey, email);
            } else {
                localStorage.removeItem(this.rememberedEmailKey);
            }

            return {
                user: mockUser,
                token
            };
        }

        throw new Error('error.auth.invalidCredentials');
    }

    async logout(): Promise<void> {
        this.clearTokens();
    }

    async refreshToken(): Promise<string> {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            throw new Error('error.auth.noRefreshTokenAvailable');
        }

        // TODO: Implement actual refresh token logic with API
        // For now, just return a new mock token
        const mockUser = {
            id: '1',
            email: 'dennis@psinfoodservice.com',
            username: 'Dennis',
            avatar: 'https://via.placeholder.com/150'
        };
        const newToken = this.generateMockToken(mockUser);
        this.setToken(newToken);
        return newToken;
    }

    getToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshTokenKey);
    }

    isTokenValid(): boolean {
        const token = this.getToken();
        if (!token) return false;

        try {
            const decoded = jwtDecode<DecodedToken>(token);
            return decoded.exp * 1000 > Date.now();
        } catch {
            return false;
        }
    }

    getRememberedEmail(): string | null {
        return localStorage.getItem(this.rememberedEmailKey);
    }

    private setTokens(token: string, refreshToken: string): void {
        localStorage.setItem(this.tokenKey, token);
        localStorage.setItem(this.refreshTokenKey, refreshToken);
    }

    private setToken(token: string): void {
        localStorage.setItem(this.tokenKey, token);
    }

    private clearTokens(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.refreshTokenKey);
    }

    private generateMockToken(user: { id: string; email: string; username: string }): string {
        // This is just for mock purposes - in production, tokens would come from the server
        const header = {
            alg: 'HS256',
            typ: 'JWT'
        };
        
        const payload = {
            sub: user.id,
            email: user.email,
            username: user.username,
            exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour expiration
        };

        // Encode header and payload
        const encodedHeader = btoa(JSON.stringify(header));
        const encodedPayload = btoa(JSON.stringify(payload));
        
        // Create a mock signature (in production, this would be a real HMAC signature)
        const signature = btoa('mock-signature');

        // Combine all parts
        return `${encodedHeader}.${encodedPayload}.${signature}`;
    }

    private generateMockRefreshToken(): string {
        return btoa(Math.random().toString()); // Simple mock refresh token
    }

    async loginWithOAuth(provider: OAuthProvider): Promise<AuthResponse> {
        // Generate a random state for OAuth security
        const state = this.generateRandomState();
        localStorage.setItem(this.oauthStateKey, state);

        // Construct the OAuth URL based on the provider
        const oauthUrl = this.getOAuthUrl(provider, state);

        // Open the OAuth popup
        const popup = window.open(oauthUrl, 'oauth-popup', 'width=600,height=600');
        if (!popup) {
            throw new Error('error.auth.popupBlocked');
        }

        // Wait for the OAuth callback
        return new Promise((resolve, reject) => {
            const handleMessage = async (event: MessageEvent) => {
                // Verify the origin of the message
                if (event.origin !== window.location.origin) return;

                // Check if the message contains OAuth data
                if (event.data.type === 'oauth-callback') {
                    window.removeEventListener('message', handleMessage);
                    popup.close();

                    // Verify state to prevent CSRF attacks
                    const receivedState = event.data.state;
                    const storedState = localStorage.getItem(this.oauthStateKey);
                    localStorage.removeItem(this.oauthStateKey);

                    if (receivedState !== storedState) {
                        reject(new Error('error.auth.invalidOAuthState'));
                        return;
                    }

                    try {
                        // Exchange the code for tokens
                        const response = await this.exchangeOAuthCode(provider, event.data.code);
                        resolve(response);
                    } catch (error) {
                        reject(error);
                    }
                }
            };

            window.addEventListener('message', handleMessage);

            // Handle popup closed
            const checkPopup = setInterval(() => {
                if (popup.closed) {
                    clearInterval(checkPopup);
                    window.removeEventListener('message', handleMessage);
                    reject(new Error('error.auth.popupClosed'));
                }
            }, 1000);
        });
    }

    private getOAuthUrl(provider: OAuthProvider, state: string): string {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const redirectUri = `${window.location.origin}/auth/oauth-callback`;
        
        switch (provider) {
            case 'google':
                return `${baseUrl}/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
            case 'microsoft':
                return `${baseUrl}/auth/microsoft?redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
            case 'linkedin':
                return `${baseUrl}/auth/linkedin?redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
            default:
                throw new Error('error.auth.unsupportedOAuthProvider');
        }
    }

    private async exchangeOAuthCode(provider: OAuthProvider, code: string): Promise<AuthResponse> {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const redirectUri = `${window.location.origin}/auth/oauth-callback`;

        const response = await fetch(`${baseUrl}/auth/${provider}/callback`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                code,
                redirect_uri: redirectUri,
            }),
        });

        if (!response.ok) {
            throw new Error('error.auth.failedToExchangeOAuthCode');
        }

        const data = await response.json();
        this.setTokens(data.token, data.refreshToken);
        return data;
    }

    private generateRandomState(): string {
        return Math.random().toString(36).substring(2, 15);
    }
}

export const authService = AuthService.getInstance(); 